
// Shared small-dialog primitives + the ONE shared dialog skin (pvp-*) for Digitalisering's own Potree dialogs.
// The skin is the light PointView application-UI look (--pv-* tokens from the host with standalone fallback
// values); tool dialogs open top-left in the viewer area (no dimming, transparent modal blocker), see
// createDialogOverlay() below. Factored out of VectorEditingMenu.js into a dependency-free module so BOTH
// VectorEditingMenu.js and the parametric-construction tool files (tools/circleTool.js, rectangleTool.js,
// arcTool.js, labeledAreaTool.js) can import them without a circular import.

// Marks an element as Digitalisering's own popup UI (a dialog overlay, a
// vertex/segment context menu, a result panel, ...) - every such root
// element gets this attribute, and DigitizationModule.js's single
// document-level contextmenu listener uses it to also suppress the
// browser's native right-click menu there. Needed because these overlays
// are appended to document.body (siblings of the viewer, not descendants
// of it - see createDialogOverlay() below), so the existing
// viewer.renderArea-scoped contextmenu block (InputHandler.js) never sees
// them (bruger-krav: "gælder alle" Digitalisering-dialoger/-menuer, ikke
// kun selve punktsky-området).
import {hostDialogAvailable, requestHostConfirm, htmlMessageToText} from "./HostDialog.js";

import {t} from "./i18n/index.js";
export const DIGITIZATION_UI_ATTR = 'data-digitization-ui';
export function markDigitizationUI(el){
	el.setAttribute(DIGITIZATION_UI_ATTR, '');
	return el;
}

// Fælles Potree-dialogskin (PointViews overlay-kontrakt, se frontendens VIEWER-UI-OVERLAYS.md): lys surface, --pv-primary,
// radius, skygge, header/body/actions, intern body-scroll. Potree og Vue deler samme document, så skinnet læser frontendens
// --pv-*-variabler; hver værdi har en fallback, så standalone Potree (uden Vue-CSS) ser rigtig ud. Injiceres én gang.
const PVP_STYLE_ID = 'pvp-dialog-style';
const PVP_CSS = `
.pvp-overlay{position:fixed;inset:0;z-index:10002;background:transparent;font-family:Roboto,Arial,sans-serif}
.pvp-overlay--modal{background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center}
.pvp-dialog{position:absolute;display:flex;flex-direction:column;box-sizing:border-box;overflow:hidden;
 background:var(--pv-surface,#fff);color:rgba(0,0,0,0.87);border-radius:4px;font-size:13px;line-height:1.4;
 box-shadow:var(--pv-elevation-overlay,0 7px 8px -4px rgba(0,0,0,0.2),0 12px 17px 2px rgba(0,0,0,0.14),0 5px 22px 4px rgba(0,0,0,0.12))}
.pvp-overlay--modal .pvp-dialog{position:static}
.pvp-head{flex:0 0 auto;padding:12px 16px;border-bottom:1px solid var(--pv-border,rgba(0,0,0,0.08));
 font-size:var(--pv-title-size,15px);font-weight:var(--pv-title-weight,500)}
.pvp-body{flex:1 1 auto;min-height:0;overflow-y:auto;padding:16px}
.pvp-actions{flex:0 0 auto;display:flex;align-items:center;justify-content:flex-end;gap:8px;padding:8px 12px;border-top:1px solid var(--pv-border,rgba(0,0,0,0.08))}
.pvp-desc{margin:0 0 12px;color:var(--pv-text-muted,rgba(0,0,0,0.55))}
.pvp-label{display:block;margin:0 0 4px;font-size:12px;color:var(--pv-text-muted,rgba(0,0,0,0.55))}
.pvp-input{display:block;width:100%;box-sizing:border-box;margin:0 0 12px;padding:7px 8px;border:1px solid rgba(0,0,0,0.38);
 border-radius:4px;background:#fff;color:inherit;font:inherit}
.pvp-input:focus{outline:none;border-color:var(--pv-primary,#0073A9);box-shadow:0 0 0 1px var(--pv-primary,#0073A9)}
.pvp-input--color{height:34px;padding:2px}
.pvp-row{display:flex;flex-wrap:wrap;gap:6px;margin:0 0 12px}
.pvp-row .pvp-input{margin:0;flex:1 1 auto;width:auto}
.pvp-note{margin:-4px 0 12px;font-size:11px;color:var(--pv-text-muted,rgba(0,0,0,0.55))}
.pvp-error{min-height:14px;margin:-6px 0 8px;font-size:11px;color:var(--pv-danger,#ff5252)}
.pvp-btn{padding:6px 12px;border:none;border-radius:4px;background:transparent;color:inherit;font:inherit;font-weight:500;cursor:pointer}
.pvp-btn:hover{background:var(--pv-hover,rgba(0,0,0,0.05))}
.pvp-btn--primary{color:var(--pv-primary,#0073A9)}
.pvp-btn--start{margin-right:auto}
.pvp-chip{flex:1 1 auto;padding:4px 8px;border:1px solid rgba(0,0,0,0.38);border-radius:4px;background:#fff;color:inherit;font:inherit;cursor:pointer}
.pvp-chip:hover{background:var(--pv-hover,rgba(0,0,0,0.05))}
.pvp-chip.is-active{border-color:var(--pv-primary,#0073A9);background:var(--pv-active,rgba(0,115,169,0.08));color:var(--pv-primary,#0073A9)}
.pvm-menu{position:fixed;z-index:10001;box-sizing:border-box;min-width:200px;
 max-width:min(340px,calc(100vw - 2*var(--pv-viewport-margin,16px)));max-height:calc(100vh - 2*var(--pv-viewport-margin,16px));
 overflow-y:auto;padding:6px 8px;background:var(--pv-surface,#fff);color:rgba(0,0,0,0.87);border-radius:4px;
 font-family:Roboto,Arial,sans-serif;font-size:14px;line-height:1.3;
 box-shadow:var(--pv-elevation-overlay,0 7px 8px -4px rgba(0,0,0,0.2),0 12px 17px 2px rgba(0,0,0,0.14),0 5px 22px 4px rgba(0,0,0,0.12))}
.pvm-title{margin:-6px -8px 4px;padding:10px 16px;border-bottom:1px solid var(--pv-border,rgba(0,0,0,0.08));
 font-size:var(--pv-title-size,15px);font-weight:var(--pv-title-weight,500)}
.pvm-band{margin:6px -8px 4px;padding:8px 16px;font-size:var(--pv-band-size,12px);font-weight:700;letter-spacing:0.06em;
 text-transform:uppercase;color:rgba(0,0,0,0.65);background:var(--pv-band-bg,rgba(0,0,0,0.03));
 border-top:1px solid var(--pv-border,rgba(0,0,0,0.08));border-bottom:1px solid var(--pv-border,rgba(0,0,0,0.08))}
.pvm-title + .pvm-band{margin-top:0;border-top:none}
.pvm-sep{height:1px;margin:4px 0;background:var(--pv-border,rgba(0,0,0,0.08))}
.pvm-item{position:relative;display:flex;align-items:center;min-height:36px;box-sizing:border-box;padding:6px 16px;
 border-radius:var(--pv-radius-row,6px);cursor:pointer}
.pvm-item:hover,.pvm-item.is-open{background:var(--pv-hover,rgba(0,0,0,0.05))}
.pvm-item.is-disabled{opacity:0.5;cursor:default}
.pvm-item.is-disabled:hover{background:transparent}
.pvm-chevron{margin-left:auto;padding-left:16px;opacity:0.6}
`;

export function ensurePvpStyle(){
	if(document.getElementById(PVP_STYLE_ID)) return;
	const style = document.createElement('style');
	style.id = PVP_STYLE_ID;
	style.textContent = PVP_CSS;
	document.head.appendChild(style);
}

export function pvViewportMargin(){
	const raw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--pv-viewport-margin'));
	return isFinite(raw) && raw >= 0 ? raw : 16;
}

// ONE shared overlay+box constructor for EVERY geometry-input popup dialog in Digitalisering - so every dialog's POSITION
// is decided in exactly one place.
//
// Viewer-input/tool-dialog (standard): åbner NÆR ØVERSTE VENSTRE HJØRNE af det faktiske synlige viewer-område
// (`dm.viewer.renderArea`s rect, skåret til viewporten - ikke window (0,0): topbar/sidebar forskyder området), med
// --pv-viewport-margin fra venstre og fra toppen - toppen regnet fra BUNDEN af viewer-toolbaren (frontendens .tb-toolbar,
// en overlay over viewer-området), så dialogen ligger under den (samme afstand som til venstre); uden en toolbar (standalone
// Potree) fra viewer-områdets top. Ingen visuel dæmpning (geometrien skal kunne ses) - men overlayet er en
// TRANSPARENT blocker, der fanger al input (som den tidligere backdrop gjorde), så klik udenfor hverken kasserer prompten,
// ændrer geometri eller aktiverer et andet værktøj. Boksen er begrænset til viewporten (max-højde/-bredde); header og actions
// bliver stående, brødteksten ruller (se fillDialog()).
//
// {center: true}: almindelig modal besked (information) - centreret, med den sædvanlige dæmpning.
// Bunden af viewer-toolbaren (værts-appens .tb-toolbar), eller 0 uden en synlig toolbar.
function viewerToolbarBottom(){
	const bar = document.querySelector('.tb-toolbar');
	if(!bar || !bar.getBoundingClientRect){ return 0; }
	const rect = bar.getBoundingClientRect();
	return rect.height > 0 ? rect.bottom : 0;
}

export function createDialogOverlay(dm, boxWidthPx, {center = false} = {}){
	ensurePvpStyle();
	const overlay = document.createElement('div');
	overlay.className = center ? 'pvp-overlay pvp-overlay--modal' : 'pvp-overlay';

	const box = document.createElement('div');
	box.className = 'pvp-dialog';
	overlay.appendChild(box);
	markDigitizationUI(overlay);
	document.body.appendChild(overlay);
	// så højreklik-i-idle-reglen ikke forlader værktøjet bag en åben dialog (se DigitizationModule._trackToolDialog)
	if(dm && typeof dm._trackToolDialog === 'function') dm._trackToolDialog(overlay);

	const place = () => {
		if(!overlay.isConnected){ window.removeEventListener('resize', place); return; }
		const margin = pvViewportMargin();
		const vw = window.innerWidth, vh = window.innerHeight;
		if(center){
			box.style.width = `${Math.min(boxWidthPx, vw - 2 * margin)}px`;
			box.style.maxHeight = `${vh - 2 * margin}px`;
			return;
		}
		const rect = dm.viewer.renderArea.getBoundingClientRect();
		const left = Math.min(Math.max(rect.left, 0) + margin, Math.max(0, vw - margin - 120));
		const top = Math.min(Math.max(rect.top, viewerToolbarBottom(), 0) + margin, Math.max(0, vh - margin - 120));
		box.style.left = `${Math.round(left)}px`;
		box.style.top = `${Math.round(top)}px`;
		box.style.width = `${Math.min(boxWidthPx, vw - left - margin)}px`;
		box.style.maxHeight = `${vh - top - margin}px`;
	};
	place();
	window.addEventListener('resize', place);
	return {overlay, box};
}

// Fælles opbygning af indholdet: header (titel), body (ruller internt) og actions (bliver stående).
// description/body/actions er HTML-strenge (skinnets klasser: pvp-desc, pvp-label, pvp-input, pvp-btn, pvp-btn--primary, ...).
export function fillDialog(box, {title, description = '', body = '', actions = ''}){
	box.innerHTML = `<div class="pvp-head">${title}</div>` +
		`<div class="pvp-body">${description ? `<p class="pvp-desc">${description}</p>` : ''}${body}</div>` +
		(actions ? `<div class="pvp-actions">${actions}</div>` : '');
}

export const pvpButton = (action, label, kind = '') =>
	`<button type="button" class="pvp-btn${kind ? ` pvp-btn--${kind}` : ''}" data-action="${action}">${label}</button>`;

export function showGeometryWarning(dm, title, message){
	// Vært-UI (PointViews frontend): almindelig besked (kun "Luk", ingen sideeffekt). Uden vært: DOM-dialogen herunder.
	if(hostDialogAvailable(dm.viewer)){
		requestHostConfirm(dm.viewer, {kind: 'message', size: 's', title, text: htmlMessageToText(message)});
		return;
	}
	const {overlay, box} = createDialogOverlay(dm, 340, {center: true});
	fillDialog(box, {title, body: `<div style="line-height:1.5">${message}</div>`, actions: pvpButton('ok', t('Luk'), 'primary')});

	const close = () => document.body.contains(overlay) && document.body.removeChild(overlay);
	box.querySelector('[data-action="ok"]').addEventListener('click', close);
	overlay.addEventListener('mousedown', (e) => { if(e.target === overlay) close(); });
	box.setAttribute('tabindex', '-1');
	box.addEventListener('keydown', (e) => { if(e.key === 'Escape'){ e.stopPropagation(); close(); } });
	box.focus();
}

// Danish, fixed-decimal display of a length in the project's own unit -
// matches the precision already used throughout Digitalisering's coordinate
// labels (see DigitizationStyle.js/CopyFeedback.js's own .toFixed(3)).
export function formatLength(value){
	return `${value.toFixed(3)} m`;
}
